import React, { Dispatch, SetStateAction, useState } from "react";

import axios from "axios";
import { uuidv7 as uuid } from "uuidv7";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Button } from "src/app/components/Button/Button";
import { ReactComponent as RubberIcon } from "src/images/rubber.svg";
import { ReactComponent as PlusIcon } from "src/images/plus-dark-grey.svg";
import { ReactComponent as RocketIcon } from "src/images/rocket-grey.svg";
import { ReactComponent as RocketCheckedIcon } from "src/images/rocket-grey-checked.svg";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { API_URLS } from "src/utils/API_URLS";
import { generateRankString } from "src/utils/rankStrings";
import { TaskType } from "src/types";
import { showToast } from "src/app/methods/showToast";
import { RootState } from "src/redux/reducers";
import { SelectCampaignModal } from "src/app/modals/SelectCampaignModal/SelectCampaignModal";
import { SelectOption } from "src/app/modals/CreateNewProjectPane/types";
import { setCreateNewProjectData } from "src/redux/main/mainActions";
import { navigateToCreateNewProject } from "src/app/SettingsModal/methods";

import "./GalleryToolbar.scss";

interface Props {
  selectedPostIds: string[];
  setSelectedPostIds: Dispatch<SetStateAction<string[]>>;
}

export const GalleryToolbar = (props: Props) => {
  const { selectedPostIds, setSelectedPostIds } = props;

  const [showSelectCampaignModal, setShowSelectCampaignModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<SelectOption | null>(
    null,
  );

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const dispatch = useDispatch();
  const history = useHistory();

  const { projectId: projectIdFromParams } = useParams<{ projectId: string }>();

  const addToCampaign = async (projectId: string) => {
    const publicationUrls = selectedPostIds;
    let previousRank = "";

    const tasksData = publicationUrls
      .filter((name: string) => name.length)
      .map((name: string) => {
        const newRank = generateRankString(previousRank, "");
        previousRank = newRank;

        return {
          uuid: uuid(),
          name,
          rank: newRank,
        };
      });

    setSelectedPostIds([]);

    axios
      .post(API_URLS.createMultipleTasks, {
        wsTaskWorkspaceUuid: activeWorkspaceUuid,
        projectId,
        taskType: TaskType.Publication,
        tasksData,
        defaultMetaFieldsValues: [],
      })
      .then(() => {
        showToast(
          "success",
          <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
          <IDHFormattedMessage
            id="ws_publications_added_to_the_selected_campaign"
            defaultMessage="Publications added to the selected campaign."
          />,
        );
      })
      .catch((err) => {
        if (err.response?.data?.errors?.length > 0) {
          showToast(
            "error",
            <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
            <IDHFormattedMessage
              id={err?.response?.data?.errors[0]?.message}
            />,
          );
        }
      });
  };

  const dispatchDataAndNavigateToCreateNewProject = () => {
    const publicationUrls = selectedPostIds;
    let previousRank = "";

    const selectedCreatorsPayload = publicationUrls
      .filter((name: string) => name.length)
      .map((name: string) => {
        const newRank = generateRankString(previousRank, "");
        previousRank = newRank;

        return {
          uuid: uuid(),
          name,
          rank: newRank,
        };
      });

    dispatch(
      setCreateNewProjectData({
        selectedCreatorsPayload,
        taskType: TaskType.Publication,
      }),
    );
    setShowSelectCampaignModal(false);
    navigateToCreateNewProject(history, activeWorkspaceUuid);
  };

  return (
    <div className="gallery-toolbar">
      <span className="gallery-toolbar__counter">
        <IDHFormattedMessage id="ws_selected" defaultMessage="Selected" />
        <span className="gallery-toolbar__counter-number">
          {selectedPostIds.length}
        </span>
      </span>

      <span className="gallery-toolbar__separator" />

      <span className="gallery-toolbar__buttons">
        <Dropdown toBottom>
          {(isOpen, setIsOpen) => (
            <>
              <Button
                size="medium"
                variant="white"
                onClick={() => setIsOpen((prev) => !prev)}
                dataQa="add-to-button"
              >
                <PlusIcon />
                <IDHFormattedMessage id="ws_add_to" defaultMessage="Add to" />
              </Button>
              <DropdownMenu isOpen={isOpen}>
                {projectIdFromParams && (
                  <DropdownMenuItem
                    onClick={() => addToCampaign(projectIdFromParams)}
                    data-qa="this-campaign-button"
                  >
                    <RocketCheckedIcon />
                    <IDHFormattedMessage
                      id="ws_this_campaign"
                      defaultMessage="This campaign"
                    />
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setShowSelectCampaignModal(true)}
                  data-qa="campaign-button"
                >
                  <RocketIcon />
                  {projectIdFromParams ? (
                    <IDHFormattedMessage
                      id="ws_another_campaign"
                      defaultMessage="Another campaign"
                    />
                  ) : (
                    <IDHFormattedMessage
                      id="ws_campaign"
                      defaultMessage="Campaign"
                    />
                  )}
                </DropdownMenuItem>
              </DropdownMenu>
            </>
          )}
        </Dropdown>

        <Button
          size="medium"
          variant="white"
          onClick={() => setSelectedPostIds([])}
          dataQa="clear-button"
        >
          <RubberIcon />
          <IDHFormattedMessage id="ws_clear" defaultMessage="Clear" />
        </Button>
      </span>

      {showSelectCampaignModal && (
        <SelectCampaignModal
          confirm={{
            action: (projectUuid: string) => addToCampaign(projectUuid),
            text: (
              <IDHFormattedMessage
                id="ws_confirm_gallery"
                defaultMessage="Confirm"
              />
            ),
          }}
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={setSelectedCampaign}
          onClose={() => setShowSelectCampaignModal(false)}
          openCreateNewProjectModal={dispatchDataAndNavigateToCreateNewProject}
        />
      )}
    </div>
  );
};
